import { useState } from "react";
import {
  Bell,
  Bot,
  KeyRound,
  LogOut,
  Search,
  UserCircle,
  X,
} from "lucide-react";
import ThemeButton from "./ThemeButton";
import { useAuth } from "../../auth/AuthContext";

const API_BASE = "/api/v1";

const ROLE_LABELS = {
  comp_admin:         "Comp Admin",
  finance:            "Finance",
  manager:            "Manager",
  rep:                "Rep",
  tenant_super_admin: "Tenant Super Admin",
  platform_admin:     "Platform Admin",
  tool_admin:         "Tool Admin",
};

// ---------------------------------------------------------------------------
// ChangePasswordModal — authenticated self-service password change
// spec_auth_identity_s661.md §3.4 — Path D
// Only rendered for auth_method === 'local' users.
// ---------------------------------------------------------------------------

function checkComplexity(pw) {
  if (pw.length < 10)     return "Password must be at least 10 characters.";
  if (!/[A-Z]/.test(pw)) return "Must contain at least one uppercase letter.";
  if (!/[a-z]/.test(pw)) return "Must contain at least one lowercase letter.";
  if (!/[0-9]/.test(pw)) return "Must contain at least one number.";
  return null;
}

function errorFromCode(code, message) {
  switch (code) {
    case "INVALID_CREDENTIALS":
      return "Current password is incorrect.";
    case "PASSWORD_COMPLEXITY_VIOLATION":
      return "New password does not meet complexity requirements.";
    case "PASSWORD_REUSE_DENIED":
      return "New password cannot be the same as your current password.";
    case "AUTH_METHOD_INCOMPATIBLE":
      return "Password change is not available for your login method.";
    default:
      return message || "Something went wrong. Please try again.";
  }
}

function ChangePasswordModal({ onClose }) {
  const [current,        setCurrent]        = useState("");
  const [newPw,          setNewPw]          = useState("");
  const [confirmPw,      setConfirmPw]      = useState("");
  const [showCurrent,    setShowCurrent]    = useState(false);
  const [showNew,        setShowNew]        = useState(false);
  const [submitting,     setSubmitting]     = useState(false);
  const [error,          setError]          = useState(null);
  const [success,        setSuccess]        = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (newPw !== confirmPw) {
      setError("New passwords do not match.");
      return;
    }
    const complexityError = checkComplexity(newPw);
    if (complexityError) {
      setError(complexityError);
      return;
    }
    if (current === newPw) {
      setError("New password cannot be the same as your current password.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method:      "POST",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify({
          current_password:     current,
          new_password:         newPw,
          new_password_confirm: confirmPw,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(errorFromCode(data?.error?.code ?? "", data?.error?.message ?? ""));
        return;
      }

      setSuccess(true);
    } catch (_) {
      setError("Unable to connect. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  // Close on backdrop click
  function handleBackdrop(e) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label="Change password"
    >
      <div className="w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-xl">

        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div>
            <div className="text-tok-base font-bold text-[rgb(var(--color-text))]">
              Change password
            </div>
            <div className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
              Your current session stays active after changing.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] transition"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {success ? (
          /* ── Success state ── */
          <div className="space-y-4">
            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4 text-center">
              <div className="text-2xl mb-2">✓</div>
              <div className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
                Password updated
              </div>
              <div className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-1">
                Your password has been changed successfully.
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-xl bg-[rgb(var(--color-primary))] py-2.5 text-tok-sm font-semibold text-white hover:opacity-90 transition"
            >
              Done
            </button>
          </div>
        ) : (
          /* ── Form ── */
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>

            {error && (
              <div className="rounded-xl border border-[rgb(var(--color-danger))]/20 bg-[rgb(var(--color-danger))]/5 px-3 py-2.5 text-tok-sm text-[rgb(var(--color-danger))]">
                {error}
              </div>
            )}

            {/* Current password */}
            <div className="space-y-1">
              <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
                Current password
              </label>
              <div className="relative flex items-center">
                <input
                  type={showCurrent ? "text" : "password"}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                  required
                  disabled={submitting}
                  placeholder="••••••••••"
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 pr-14 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-3 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                  tabIndex={-1}
                >
                  {showCurrent ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {/* New password */}
            <div className="space-y-1">
              <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
                New password
              </label>
              <div className="relative flex items-center">
                <input
                  type={showNew ? "text" : "password"}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={submitting}
                  placeholder="••••••••••"
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 pr-14 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-3 text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
                  tabIndex={-1}
                >
                  {showNew ? "Hide" : "Show"}
                </button>
              </div>
              <div className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                10+ characters · uppercase · lowercase · number
              </div>
            </div>

            {/* Confirm new password */}
            <div className="space-y-1">
              <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
                Confirm new password
              </label>
              <div className="relative flex items-center">
                <input
                  type={showNew ? "text" : "password"}
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={submitting}
                  placeholder="••••••••••"
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={submitting || !current || !newPw || !confirmPw}
                className="flex-1 rounded-xl bg-[rgb(var(--color-primary))] py-2.5 text-tok-sm font-semibold text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Updating…" : "Update password"}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-2.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] transition disabled:opacity-50"
              >
                Cancel
              </button>
            </div>

          </form>
        )}

      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// IdentityMenu
// ---------------------------------------------------------------------------

function IdentityMenu({ user, onLogout }) {
  const name = user?.full_name || user?.email || "DecisionFlow User";
  const role = ROLE_LABELS[user?.user_type] || user?.user_type || "User";

  // Change password only available to local auth users
  const canChangePassword = user?.auth_method === "local";
  const [showChangePw, setShowChangePw] = useState(false);

  return (
    <>
      <div className="flex items-center gap-3">
        <div className="hidden text-right sm:block">
          <div className="text-tok-sm font-medium text-[rgb(var(--color-text))]">
            {name}
          </div>
          <div className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            {role}
          </div>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text))]">
          <UserCircle size={18} aria-hidden="true" />
        </div>

        {/* Change password — local auth users only */}
        {canChangePassword && (
          <button
            type="button"
            onClick={() => setShowChangePw(true)}
            className="inline-flex items-center gap-1 rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
            aria-label="Change password"
          >
            <KeyRound size={14} aria-hidden="true" />
            Password
          </button>
        )}

        <button
          type="button"
          onClick={onLogout}
          className="inline-flex items-center gap-1 rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
        >
          <LogOut size={14} aria-hidden="true" />
          Logout
        </button>
      </div>

      {/* Change password modal — portal-free, renders above all shells */}
      {showChangePw && (
        <ChangePasswordModal onClose={() => setShowChangePw(false)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// TopBar — unchanged except IdentityMenu now includes modal
// ---------------------------------------------------------------------------

export default function TopBar({
  title = "DecisionFlow",
  tenantName,
  subtitle,
  searchValue = "",
  onSearchChange,
  showSearch = true,
  showAlerts = true,
  showCopilot = true,
  copilotOpen = false,
  onToggleCopilot,
  killSwitchActive = false,
  mode,
  onModeChange,
  children,
}) {
  const { user, logout } = useAuth();
  const isDev = import.meta.env.VITE_APP_MODE === "dev";

  const displayName   = tenantName ?? user?.tenant_name ?? title;
  const tenantInitial = displayName.charAt(0).toUpperCase();

  return (
    <header className="flex min-h-16 items-center justify-between gap-4 border-b border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[rgb(var(--color-primary))] text-white text-tok-sm font-bold select-none">
          {tenantInitial}
        </div>
        <div className="min-w-0">
          <div className="truncate text-tok-base font-bold text-[rgb(var(--color-text))]">
            {displayName}
          </div>
          <div className="truncate text-tok-xs text-[rgb(var(--color-text-muted))]">
            DecisionFlow by data-fit LLC
          </div>
        </div>
      </div>

      {showSearch && (
        <div className="hidden flex-1 justify-center lg:flex">
          <div className="relative w-full max-w-xl">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[rgb(var(--color-text-muted))]"
              aria-hidden="true"
            />
            <input
              type="search"
              value={searchValue}
              onChange={(event) => onSearchChange?.(event.target.value)}
              placeholder="Search DecisionFlow"
              className="w-full rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] py-2 pl-9 pr-3 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))]"
            />
          </div>
        </div>
      )}

      <div className="flex shrink-0 items-center gap-2">
        {children}

        {isDev && onModeChange && (
          <button
            type="button"
            onClick={onModeChange}
            className="rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-2 py-1.5 text-tok-xs font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
          >
            {mode || "Mode"}
          </button>
        )}

        {showAlerts && (
          <button
            type="button"
            className="rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-2 text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
            aria-label="Alerts"
          >
            <Bell size={16} aria-hidden="true" />
          </button>
        )}

        <ThemeButton />

        {showCopilot && (
          <button
            type="button"
            onClick={killSwitchActive ? undefined : onToggleCopilot}
            disabled={killSwitchActive}
            title={killSwitchActive ? "Agent halted — kill switch active" : undefined}
            className={`rounded-md border px-3 py-2 text-tok-sm font-medium transition ${
              killSwitchActive
                ? "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))] opacity-50 cursor-not-allowed"
                : copilotOpen
                  ? "border-[rgb(var(--color-primary))] bg-[rgb(var(--color-primary))] text-white"
                  : "border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))]"
            }`}
            aria-pressed={!killSwitchActive && copilotOpen}
            aria-disabled={killSwitchActive}
          >
            <span className="inline-flex items-center gap-2">
              <Bot size={16} aria-hidden="true" />
              Copilot
            </span>
          </button>
        )}

        <IdentityMenu user={user} onLogout={logout} />
      </div>
    </header>
  );
}
